import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Archive, CheckCircle2, Clock, Loader2, AlertTriangle, Unlock } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

type EstadoCiclo = 'ACTIVA' | 'CANCELACION_PENDIENTE' | 'ARCHIVADA' | 'PURGADA_TOTAL';

const CICLO_BADGE: Record<EstadoCiclo, { label: string; cls: string }> = {
  ACTIVA:                { label: 'Activa',               cls: 'bg-emerald-900 text-emerald-300 border-emerald-700' },
  CANCELACION_PENDIENTE: { label: 'Cancelación pendiente', cls: 'bg-amber-900 text-amber-300 border-amber-700' },
  ARCHIVADA:             { label: 'Archivada',            cls: 'bg-orange-900 text-orange-300 border-orange-700' },
  PURGADA_TOTAL:         { label: 'Purgada (total)',      cls: 'bg-red-900 text-red-400 border-red-700' },
};

function fmtDate(v?: string | null) {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function CicloVidaClinicaModal({
  clinica,
  onClose,
}: {
  clinica: { id: string; nombre: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [diasGracia, setDiasGracia]   = useState(90);
  const [diasAcceso, setDiasAcceso]   = useState(30);
  const [confirmStep, setConfirmStep] = useState<null | 'cancelar' | 'acceso'>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['ciclo-vida', clinica.id],
    queryFn: () =>
      api.get(`/api/ciclo-vida/clinicas/${clinica.id}`).then((r) => r.data),
    staleTime: 0,
  });

  function invalidar() {
    qc.invalidateQueries({ queryKey: ['ciclo-vida', clinica.id] });
    qc.invalidateQueries({ queryKey: ['sa-clinicas'] });
  }

  const iniciarMut = useMutation({
    mutationFn: () =>
      api.post(`/api/ciclo-vida/clinicas/${clinica.id}/iniciar-cancelacion`, { diasGracia }).then((r) => r.data),
    onSuccess: () => { toast.success('Período de gracia iniciado'); invalidar(); setConfirmStep(null); },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error')),
  });

  const revocarMut = useMutation({
    mutationFn: () =>
      api.post(`/api/ciclo-vida/clinicas/${clinica.id}/revocar-cancelacion`).then((r) => r.data),
    onSuccess: () => { toast.success('Cancelación revocada — clínica reactivada'); invalidar(); },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error')),
  });

  const accesoMut = useMutation({
    mutationFn: () =>
      api.patch(`/api/ciclo-vida/clinicas/${clinica.id}/acceso-lectura`, { diasAcceso }).then((r) => r.data),
    onSuccess: () => { toast.success(`Acceso de lectura concedido por ${diasAcceso} días`); invalidar(); setConfirmStep(null); },
    onError: (e: any) => toast.error(getErrorMessage(e, 'Error')),
  });

  const estado: EstadoCiclo = data?.estadoCiclo ?? 'ACTIVA';
  const badge = CICLO_BADGE[estado] ?? CICLO_BADGE['ACTIVA'];
  const pending = iniciarMut.isPending || revocarMut.isPending || accesoMut.isPending;

  return (
    <Dialog.Root open onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed z-50 inset-x-0 bottom-0 md:inset-x-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto w-full md:max-w-lg bg-slate-900 rounded-t-3xl md:rounded-2xl border border-slate-700 shadow-2xl p-6 space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <Dialog.Title className="text-base font-bold text-white flex items-center gap-2">
                <Archive size={16} className="text-amber-400" />
                Ciclo de vida — {clinica.nombre}
              </Dialog.Title>
              <p className="text-xs text-slate-500 mt-0.5">Gestión de retención de datos (LFPDPPP)</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-slate-500" size={24} />
            </div>
          ) : (
            <>
              {/* Estado actual */}
              <div className="bg-slate-800 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-400">Estado del ciclo</span>
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${badge.cls}`}>
                    {badge.label}
                  </span>
                </div>

                {data?.fechaCancelacionDefinitiva && (
                  <div className="flex items-center gap-2 text-xs text-amber-300">
                    <Clock size={12} />
                    <span>Archivado definitivo: {fmtDate(data.fechaCancelacionDefinitiva)}</span>
                  </div>
                )}
                {data?.fechaArchivado && (
                  <div className="flex items-center gap-2 text-xs text-orange-300">
                    <Archive size={12} />
                    <span>Archivada el: {fmtDate(data.fechaArchivado)}</span>
                  </div>
                )}
                {data?.accesoLecturaTemporalHasta && (
                  <div className="flex items-center gap-2 text-xs text-blue-300">
                    <Unlock size={12} />
                    <span>
                      Acceso temporal hasta: {fmtDate(data.accesoLecturaTemporalHasta)}
                      {new Date(data.accesoLecturaTemporalHasta) < new Date() && ' (VENCIDO)'}
                    </span>
                  </div>
                )}
              </div>

              {/* Acciones disponibles según estado */}
              {estado === 'ACTIVA' && confirmStep !== 'cancelar' && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-400">
                    Iniciar cancelación coloca a la clínica en un período de gracia antes del archivado automático.
                  </p>
                  <div className="flex items-center gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Días de gracia</label>
                      <input
                        type="number"
                        min={7}
                        max={365}
                        value={diasGracia}
                        onChange={(e) => setDiasGracia(Number(e.target.value))}
                        className="w-24 px-3 py-2 rounded-lg border border-slate-700 bg-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <button
                      onClick={() => setConfirmStep('cancelar')}
                      className="mt-5 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium transition-colors"
                    >
                      <Archive size={14} />
                      Iniciar cancelación
                    </button>
                  </div>
                </div>
              )}

              {estado === 'ACTIVA' && confirmStep === 'cancelar' && (
                <div className="bg-amber-950 border border-amber-800 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-2 text-amber-300">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <p className="text-sm">
                      ¿Confirmas iniciar el período de gracia de <strong>{diasGracia} días</strong>?
                      Después del plazo, el sistema archivará la clínica automáticamente: se eliminarán los datos personales de la clínica, se desactivarán los usuarios y se cancelará la suscripción.
                    </p>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setConfirmStep(null)}
                      className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => iniciarMut.mutate()}
                      disabled={pending}
                      className="flex items-center gap-2 px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      {iniciarMut.isPending && <Loader2 size={12} className="animate-spin" />}
                      Confirmar
                    </button>
                  </div>
                </div>
              )}

              {estado === 'CANCELACION_PENDIENTE' && (
                <div className="space-y-3">
                  <div className="bg-amber-950 border border-amber-800 rounded-xl p-4 text-sm text-amber-300">
                    La clínica será archivada automáticamente el <strong>{fmtDate(data?.fechaCancelacionDefinitiva)}</strong>.
                    Puedes revocar la cancelación antes de esa fecha.
                  </div>
                  <button
                    onClick={() => revocarMut.mutate()}
                    disabled={pending}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium transition-colors"
                  >
                    {revocarMut.isPending && <Loader2 size={12} className="animate-spin" />}
                    <CheckCircle2 size={14} />
                    Revocar cancelación
                  </button>
                </div>
              )}

              {estado === 'ARCHIVADA' && (
                <div className="space-y-3">
                  <div className="bg-slate-800 rounded-xl p-4 text-sm text-slate-400">
                    La clínica está archivada. Puedes conceder acceso temporal de solo lectura (útil para auditorías o migración de datos).
                  </div>
                  {confirmStep !== 'acceso' ? (
                    <div className="flex items-center gap-3">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Días de acceso</label>
                        <input
                          type="number"
                          min={1}
                          max={90}
                          value={diasAcceso}
                          onChange={(e) => setDiasAcceso(Number(e.target.value))}
                          className="w-24 px-3 py-2 rounded-lg border border-slate-700 bg-slate-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <button
                        onClick={() => setConfirmStep('acceso')}
                        className="mt-5 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                      >
                        <Unlock size={14} />
                        Conceder acceso
                      </button>
                    </div>
                  ) : (
                    <div className="bg-blue-950 border border-blue-800 rounded-xl p-4 space-y-3">
                      <p className="text-sm text-blue-300">
                        ¿Conceder acceso de solo lectura por <strong>{diasAcceso} días</strong>? Los usuarios de la clínica podrán consultar expedientes pero no crear ni modificar registros.
                      </p>
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setConfirmStep(null)} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors">
                          Cancelar
                        </button>
                        <button
                          onClick={() => accesoMut.mutate()}
                          disabled={pending}
                          className="flex items-center gap-2 px-4 py-1.5 bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors"
                        >
                          {accesoMut.isPending && <Loader2 size={12} className="animate-spin" />}
                          Confirmar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {estado === 'PURGADA_TOTAL' && (
                <div className="bg-slate-800 rounded-xl p-4 text-sm text-slate-400">
                  Todos los datos personales de los pacientes de esta clínica han sido anonimizados conforme al período de retención de 5 años (NOM-004-SSA3-2012). El historial clínico sin datos personales se conserva con fines de auditoría.
                </div>
              )}
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
